import i18n from "../../i18n";import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers,
  LayoutDashboard,
  Users,
  Wrench,
  UserStar,
  IdCard,
  PackageOpen,
  Handshake,
  UserRoundCheck } from
"lucide-react";
import { cn } from "../../lib/utils";
import { buildNavTree, cleanName, isPathActive } from "../../router/navigation";
import { useTranslation } from "react-i18next";

const SERVICE_ICONS = {
  FSM: Wrench,
  HRMS: Users,
  ESS: UserRoundCheck,
  CRM: IdCard,
  IMS: PackageOpen,
  PM: Handshake,
  PSM: UserStar
};

function getServiceIcon(name) {
  const words = String(name || "").trim().toUpperCase().split(/\s+/);
  const matchedKey = [...words].reverse().find((word) => SERVICE_ICONS[word]) ||
  Object.keys(SERVICE_ICONS).find((key) => words.join(" ").includes(key));
  return matchedKey ? SERVICE_ICONS[matchedKey] : Layers;
}

function isLegacyDashboardLink(to) {
  return typeof to === "string" && to.startsWith("/dashboard.html");
}

/**
 * Horizontal alternative to Sidebar. It uses the same login-provided
 * enabledServices tree. A service first shows its modules; selecting a module
 * drills into its submodule links, so the full tree is never shown at once.
 */
export function Horizontalbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navTree = useMemo(() => buildNavTree(), []);
  const [openService, setOpenService] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const barRef = useRef(null);
  const menuRef = useRef(null);
  const serviceButtonRefs = useRef({});
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    setOpenService(null);
    setOpenModule(null);
  }, [pathname]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!barRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpenService(null);
        setOpenModule(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  return (
    <nav
      ref={barRef}
      className="hrms-shell__horizontalbar relative z-40 bg-[var(--color-primary)] px-4 sm:px-6"
      aria-label={i18n.t("layout.mainNavigation")}>
      
        <div className="flex min-h-12 flex-nowrap items-center gap-1 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Link
          to="/"
          className={cn(
            "flex h-9 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm font-medium transition",
            pathname === "/" ? "bg-white/15 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"
          )}>
          
          <LayoutDashboard className="h-4 w-4" />
          {t("common.dashboard")}
        </Link>

        {navTree.map((service) => {
          const ServiceIcon = getServiceIcon(service.name);
          const serviceActive = service.modules.some((module) =>
          module.submodules.some((submodule) => isPathActive(pathname, submodule.to))
          );
          const isOpen = openService === service.name;

          return (
            <div key={service.name} className="relative shrink-0">
              <button
                type="button"
                ref={(node) => {
                  serviceButtonRefs.current[service.name] = node;
                }}
                onClick={() => {
                  if (isOpen) {
                    setOpenService(null);
                    setMenuPosition(null);
                  } else {
                    const rect = serviceButtonRefs.current[service.name]?.getBoundingClientRect();
                    if (rect) {
                      setMenuPosition({
                        left: Math.max(8, Math.min(rect.left, window.innerWidth - 272)),
                        top: rect.bottom + 8
                      });
                    }
                    setOpenService(service.name);
                  }
                  setOpenModule(null);
                }}
                className={cn(
                  "flex h-9 items-center gap-1 whitespace-nowrap rounded-lg px-3 text-sm font-medium transition",
                  serviceActive || isOpen ? "bg-white/15 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"
                )}
                aria-expanded={isOpen}>
                
                <ServiceIcon className="h-4 w-4 shrink-0" />
                {service.name}
                <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
              </button>

              {isOpen && menuPosition && createPortal(
                <div
                  ref={menuRef}
                  className="fixed z-[60] min-w-64 max-h-[min(70vh,32rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                  style={menuPosition}>
                  
                  {!openModule ?
                  <>
                      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {service.name}
                      </p>
                      {service.modules.map((module) =>
                    <button
                      key={module.name}
                      type="button"
                      onClick={() => setOpenModule(module.name)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100">
                      
                          {cleanName(module.name)}
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </button>
                    )}
                    </> :

                  (() => {
                    const module = service.modules.find((item) => item.name === openModule);
                    if (!module) return null;
                    return (
                      <>
                          <button
                          type="button"
                          onClick={() => setOpenModule(null)}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
                          
                            <ChevronLeft className="h-4 w-4" />
                            {service.name}
                          </button>
                          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {cleanName(module.name)}
                          </p>
                          {module.submodules.map((submodule) => {
                          const active = isPathActive(pathname, submodule.to);
                          const className = cn(
                            "block rounded-lg px-3 py-2 text-sm transition",
                            active ? "bg-[var(--color-accent)] text-white" : "text-slate-700 hover:bg-slate-100"
                          );
                          return isLegacyDashboardLink(submodule.to) ?
                          <a key={submodule.name} href={submodule.to} className={className}>
                                {cleanName(submodule.name)}
                              </a> :

                          <Link key={submodule.name} to={submodule.to} className={className}>
                                {cleanName(submodule.name)}
                              </Link>;

                        })}
                        </>);

                  })()
                  }
                </div>,
                document.body)}
            </div>);

        })}

        {navTree.length === 0 &&
        <span className="px-3 text-sm text-white/60">{t("common.noServices")}</span>
        }

        {/* <div className="ml-auto flex h-9 items-center gap-2 pl-3 text-white">
           <div className="flex min-w-0 items-center gap-2 text-sm">
             <UserCircle2 className="h-5 w-5 shrink-0" />
             <span className="hidden max-w-36 truncate lg:block" title={`${emp.first_name || ""} ${emp.last_name || ""}`.trim()}>
               {emp.first_name || emp.last_name ? `${emp.first_name || ""} ${emp.last_name || ""}` : initials}
             </span>
           </div>
           <button
             type="button"
             onClick={handleLogout}
             className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-rose-200 hover:bg-white/10 hover:text-white"
             title="Sign Out"
             aria-label="Sign Out"
           >
             <LogOut className="h-4 w-4" />
           </button>
          </div> */}
      </div>
    </nav>);

}
